import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinksPage = () => {
  const [links, setLinks] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    original_url: '',
    program_id: '',
    product_name: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchLinks();
    fetchPrograms();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await axios.get(`${API}/links-public`);
      setLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch links:', error);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/affiliate-programs`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      setPrograms([]);
    }
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm('Tem certeza que deseja excluir este link?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/links/${linkId}`);
      setLinks(links.filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Failed to delete link:', error);
      alert('Erro ao excluir link. Tente novamente.');
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API}/links-public`, formData);
      setFormData({ original_url: '', program_id: '', product_name: '' });
      setShowCreateForm(false);
      fetchLinks(); // Refresh the list
      alert('🎉 Link criado com sucesso! Agora você pode usar para ganhar comissões.');
    } catch (error) {
      console.error('Failed to create link:', error);
      alert('❌ Erro ao criar link. Tente novamente.');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('📋 Link copiado! Cole nas suas redes sociais para começar a ganhar.');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">🔗 Seus Links de Afiliado</h1>
          <p className="text-gray-600">Crie e gerencie links que geram comissões automaticamente</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg"
        >
          ➕ Criar Novo Link
        </button>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
        <h3 className="font-semibold text-green-800 mb-2">💡 Como Funciona (Super Simples):</h3>
        <div className="text-sm text-green-700 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <strong>1.</strong> Cole URL do produto
          </div>
          <div>
            <strong>2.</strong> Escolha programa (ClickBank = mais $$$)
          </div>
          <div>
            <strong>3.</strong> Sistema cria link rastreável
          </div>
          <div>
            <strong>4.</strong> Poste nas redes = Ganhe comissões!
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4">🚀 Criar Link de Afiliado</h3>
              <form onSubmit={handleCreateLink} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL do Produto *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.original_url}
                    onChange={(e) => setFormData({...formData, original_url: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/produto-incrivel"
                  />
                  <p className="text-xs text-gray-500 mt-1">📍 Cole aqui o link do produto que você quer promover</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Programa de Afiliado *
                  </label>
                  <select
                    required
                    value={formData.program_id}
                    onChange={(e) => setFormData({...formData, program_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione um programa</option>
                    {programs.map(program => (
                      <option key={program.id} value={program.id}>
                        💰 {program.name} ({program.commission_rate}% comissão)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">🎯 ClickBank = comissões maiores (até 75%)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Produto
                  </label>
                  <input
                    type="text"
                    value={formData.product_name}
                    onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="iPhone 15 Pro Max"
                  />
                  <p className="text-xs text-gray-500 mt-1">📝 Para identificar facilmente no dashboard</p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {creating ? '🔄 Criando...' : '🚀 Criar Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Links Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {links.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔗</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum link criado ainda</h3>
            <p className="text-gray-600 mb-4">Crie seu primeiro link de afiliado para começar a ganhar!</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg"
            >
              🚀 Criar Primeiro Link
            </button>
            
            <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
              <h4 className="font-semibold text-yellow-800 mb-2">💡 Dica Rápida:</h4>
              <p className="text-sm text-yellow-700">
                Comece com produtos do ClickBank! Eles têm as maiores comissões (até 75%) e aprovação rápida.
              </p>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {links.map(link => (
              <li key={link.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-blue-600 truncate">
                        💎 {link.product_name || 'Produto sem nome'}
                      </p>
                      <div className="ml-2 flex-shrink-0 flex">
                        <p className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          ✅ Ativo
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex">
                      <div className="flex items-center text-sm text-gray-500">
                        <p className="truncate mr-4">
                          <span className="font-medium">🔗 Link Curto:</span> {link.short_url}
                        </p>
                        <button
                          onClick={() => copyToClipboard(link.short_url)}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium"
                        >
                          📋 Copiar
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                      <span className="flex items-center">
                        <span className="mr-1">👆</span> 
                        <span className="font-medium">{link.clicks}</span> clicks
                      </span>
                      <span className="flex items-center">
                        <span className="mr-1">🎯</span>
                        <span className="font-medium">{link.conversions}</span> vendas
                      </span>
                      <span className="flex items-center">
                        <span className="mr-1">💰</span>
                        <span className="font-medium text-green-600">R$ {link.earnings.toFixed(2)}</span> ganho
                      </span>
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0 space-x-2">
                    <button
                      onClick={() => copyToClipboard(link.affiliate_url)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                    >
                      📋 Copiar Link Completo
                    </button>
                    <button
                      onClick={() => copyToClipboard(`🔥 Descobri algo incrível! ${link.product_name || 'Este produto'} está com uma oferta imperdível! Link: ${link.short_url} #afiliados #dica #oferta`)}
                      className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded text-sm font-medium"
                    >
                      📱 Copiar Post Pronto
                    </button>
                    <button
                      onClick={() => deleteLink(link.id)}
                      className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm font-medium"
                    >
                      🗑️ Excluir
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Success Tips */}
      {links.length > 0 && (
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">🚀 Como Maximizar Seus Ganhos:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">📱 Redes Sociais</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Poste no Instagram Stories diariamente</li>
                <li>• Compartilhe no Facebook em grupos</li>
                <li>• Faça vídeos no TikTok mostrando o produto</li>
              </ul>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">🎯 Melhor Horário</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Instagram: 19h-21h</li>
                <li>• Facebook: 15h-16h e 20h-21h</li>
                <li>• TikTok: 18h-20h</li>
              </ul>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">💰 Estratégia</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Use o "Copiar Post Pronto" acima</li>
                <li>• Poste 2-3x por semana por produto</li>
                <li>• Monitore cliques no dashboard</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinksPage;